import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  FulfillmentGroup,
  FulfillmentGroupData,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { ShipmentTrackerStatus } from "@redotech/redo-fulfillment-group-common/outbound-shipment-label";
import {
  UnvoidableReason,
  VoidActionSummary,
} from "@redotech/redo-fulfillment-group-common/shipment-summary";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import {
  canVoidDhlByCountriesAndAge,
  canVoidDhlByPackageId,
  canVoidProviderLabel,
} from "@redotech/redo-fulfillment-rate-common/void-eligibility";
import { Provider } from "@redotech/redo-order-common/provider";

export function canVoidFreight(row: FulfillmentGroup): boolean {
  const hasFreightShipment = !!row.shipmentDetails?.freightShipment;
  const alreadyVoided = row.shipmentDetails?.voided;
  const isClosed =
    row.status === FulfillmentGroupStatus.Closed ||
    row.status === FulfillmentGroupStatus.Cancelled;

  return !!hasFreightShipment && !alreadyVoided && isClosed;
}

export function getLabelVoidability(row: FulfillmentGroup): VoidActionSummary {
  if (!row.shipment?.boughtRate) {
    return { voidable: false, reason: UnvoidableReason.NO_LABEL };
  }
  if (row.shipment.voided) {
    return { voidable: false, reason: UnvoidableReason.ALREADY_VOIDED };
  }
  const isClosed =
    row.status === FulfillmentGroupStatus.Closed ||
    row.status === FulfillmentGroupStatus.Cancelled;
  if (!isClosed) {
    return { voidable: false, reason: UnvoidableReason.GROUP_NOT_CLOSED };
  }
  const trackerStatus = row.shipment.tracker?.status;
  const hasShippedAccordingToTracker =
    !!row.shipment.tracker?.trackingCode &&
    !!trackerStatus &&
    [
      ShipmentTrackerStatus.IN_TRANSIT,
      ShipmentTrackerStatus.STALLED_IN_TRANSIT,
      ShipmentTrackerStatus.OUT_FOR_DELIVERY,
      ShipmentTrackerStatus.DELIVERED,
    ].includes(trackerStatus);
  if (hasShippedAccordingToTracker) {
    return { voidable: false, reason: UnvoidableReason.ALREADY_SHIPPED };
  }
  if (!canVoidDhl(row) || !canVoidTiktok(row)) {
    return { voidable: false, reason: UnvoidableReason.PROVIDER_RESTRICTION };
  }
  return { voidable: true };
}

export function canVoidLabel(row: FulfillmentGroup) {
  return getLabelVoidability(row).voidable;
}

export enum UncancellableManualFulfillmentReason {
  NOT_MANUALLY_FULFILLED = "notManuallyFulfilled",
  HAS_PURCHASED_LABEL = "hasPurchasedLabel",
  GROUP_NOT_CLOSED = "groupNotClosed",
  UNSUPPORTED_PROVIDER = "unsupportedProvider",
}

/** FulfillmentGroupData flattens the fulfillment orders' providers. */
function groupProviders(
  row: FulfillmentGroupData | FulfillmentGroup,
): Provider[] {
  return "fulfillmentOrders" in row
    ? row.fulfillmentOrders.map((fo) => fo.provider)
    : (row.providers ?? []);
}

export type ManualFulfillmentCancellability =
  | { cancellable: true }
  | { cancellable: false; reason: UncancellableManualFulfillmentReason };

/**
 * Deliberately disjoint from {@link getLabelVoidability}: a group with a
 * purchased label is unwound by voiding it (which refunds), never by this path.
 */
export function getManualFulfillmentCancellability(
  row: FulfillmentGroupData | FulfillmentGroup,
): ManualFulfillmentCancellability {
  const hasPurchasedShipment =
    "fulfillmentOrders" in row
      ? row.shipment?.boughtRate || row.shipmentDetails?.freightShipment
      : row.shipmentSummary?.carrierShipments.some(
          (shipment) => shipment.purchased,
        ) || row.shipmentSummary?.freight;
  if (hasPurchasedShipment) {
    return {
      cancellable: false,
      reason: UncancellableManualFulfillmentReason.HAS_PURCHASED_LABEL,
    };
  }
  if (!row.manualFulfillmentInfo) {
    return {
      cancellable: false,
      reason: UncancellableManualFulfillmentReason.NOT_MANUALLY_FULFILLED,
    };
  }
  // A cancelled group ends up Voided, so re-cancelling fails here.
  if (row.status !== FulfillmentGroupStatus.Closed) {
    return {
      cancellable: false,
      reason: UncancellableManualFulfillmentReason.GROUP_NOT_CLOSED,
    };
  }
  // Deliberately narrow for now. Other marketplaces need their own
  // cancelFulfillment and handleVoidedGroup work before they can opt in.
  const providers = groupProviders(row);
  if (
    providers.length === 0 ||
    providers.some((provider) => provider !== Provider.REDO)
  ) {
    return {
      cancellable: false,
      reason: UncancellableManualFulfillmentReason.UNSUPPORTED_PROVIDER,
    };
  }
  return { cancellable: true };
}

export function canCancelManualFulfillment(
  row: FulfillmentGroupData | FulfillmentGroup,
) {
  return getManualFulfillmentCancellability(row).cancellable;
}

const canVoidTiktok = (row: FulfillmentGroup) => {
  const boughtRate = row.shipment?.boughtRate;
  return !boughtRate?.provider || canVoidProviderLabel(boughtRate.provider);
};

const canVoidDhl = (row: FulfillmentGroup) => {
  const boughtRate = row.shipment?.boughtRate;
  if (!boughtRate || boughtRate.provider !== RateProvider.DHL_ECOMMERCE) {
    return true;
  }

  if (
    !canVoidDhlByPackageId(boughtRate.providerInfo?.dhlEcommerce?.redoPackageId)
  ) {
    return false;
  }

  return canVoidDhlByCountriesAndAge({
    originCountry: row.summary.originAddress?.countryCode,
    destinationCountry: row.summary.destinationAddress?.countryCode,
    createdAt: row.shipment?.createdAt,
  });
};

export function getGroupDeliveryMethod(
  group: FulfillmentGroup,
): RedoShopifyDeliveryMethodType {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return (
    group.overrideDeliveryMethod ??
    (group.fulfillmentOrders.some(
      ({ deliveryMethod }) =>
        deliveryMethod.kind === RedoShopifyDeliveryMethodType.Shipping,
    )
      ? RedoShopifyDeliveryMethodType.Shipping
      : group.fulfillmentOrders[0]?.deliveryMethod?.kind)
  );
}
